import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import { Button } from 'components/Button'
import { Box, Container, Grid } from 'components/Layout'
import { Text } from 'components/Typography'
import { HUBSPOT_SCHEDULE_CALL_URL } from 'constants/common'
import { COLOR_INTENT, FONT_STACK, FONT_WEIGHT, mq, SPACE } from 'Theme'

const { TRADE_PAGE } = COLOR_INTENT.WIDGETS

const Eyebrow = styled(Text)`
  text-transform: uppercase;
  letter-spacing: 0.22em;
`

// The source shot is portrait; on desktop the image stretches to the height of
// the copy column and crops to the landscape frame from the design
const HeroImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  aspect-ratio: 4 / 5;
  object-fit: cover;
  object-position: center;

  ${mq.from.DESKTOP`
    aspect-ratio: auto;
    min-height: ${rem(460)};
  `}
`

const TradeCta = styled(Button)`
  background: ${TRADE_PAGE.CTA_BG};
  color: ${TRADE_PAGE.CTA_TEXT};

  &:hover {
    background: ${TRADE_PAGE.CTA_BG_HOVER};
  }
`

export const TradeHero: React.FC<{
  isMember: boolean
  isAuthLoading: boolean
}> = ({ isMember, isAuthLoading }) => (
  <Box backgroundColor={TRADE_PAGE.HERO_BG}>
    <Container
      py={{ MOBILE: rem(52), TABLET: rem(108) }}
      px={{ MOBILE: SPACE.PX_24, TABLET: SPACE.PX_40 }}
    >
      <Grid
        gridTemplateColumns={{
          MOBILE: '1fr',
          DESKTOP: `minmax(0, ${rem(620)}) minmax(0, 1fr)`,
        }}
        gap={{ MOBILE: rem(36), DESKTOP: rem(64) }}
      >
        <Box maxWidth={rem(620)} style={{ alignSelf: 'center' }}>
          <img
            src="/tradeExpertLogo.png"
            alt="the expert"
            style={{ height: rem(40), width: 'auto', display: 'block' }}
          />
          <Text
            as="h1"
            fontFamily={FONT_STACK.SERIF}
            fontWeight={FONT_WEIGHT.NORMAL}
            fontSize={{ MOBILE: rem(34), TABLET: rem(62) }}
            lineHeight="1.04"
            color={TRADE_PAGE.HEADING}
            mt={SPACE.PX_20}
            mb={0}
          >
            Where Trade Works
          </Text>
          <Text
            fontSize={{ MOBILE: rem(15), TABLET: rem(16) }}
            lineHeight="1.65"
            color={TRADE_PAGE.TEXT_SUB}
            my={SPACE.PX_24}
            style={{ maxWidth: rem(490) }}
            as="p"
          >
            One Trade Program. One membership. Shop Interior Define, The
            Citizenry, and 200+ additional curated brands through a single
            platform with spend that consolidates, benefits that grow, and a
            sourcing hub built for how designers actually work.
          </Text>
          <Box mb={rem(34)}>
            <Eyebrow
              fontSize={rem(10)}
              fontWeight={FONT_WEIGHT.SEMI_BOLD}
              color={TRADE_PAGE.EYEBROW}
              mb={SPACE.PX_16}
            >
              One Trade Program across
            </Eyebrow>
            <img
              src="/tradeBrandLockup.svg"
              alt="Interior Define, The Inside, The Citizenry, St. Frank, The Expert"
              style={{
                width: '100%',
                maxWidth: rem(531),
                height: 'auto',
                display: 'block',
                position: 'relative',
                left: rem(2),
              }}
            />
          </Box>
          <Box
            style={isAuthLoading ? { visibility: 'hidden' } : undefined}
            width={{ MOBILE: '100%', TABLET: 'fit-content' }}
          >
            {isMember ? (
              <TradeCta
                onClick={() => {
                  window.open(HUBSPOT_SCHEDULE_CALL_URL, '_blank')
                }}
              >
                Schedule a call to learn more
              </TradeCta>
            ) : (
              <TradeCta
                onClick={() => {
                  document
                    .getElementById('form')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
              >
                Apply Now
              </TradeCta>
            )}
          </Box>
        </Box>

        <HeroImage
          src="/tradeHero.jpg"
          alt="Bedroom styled with pieces from The Expert trade brands"
        />
      </Grid>
    </Container>
  </Box>
)
